/**
 * Utilitários para cálculo de penalizações temporais V2.0
 * Replica a lógica do backend Python para consistência
 */

import { ACTIVE_HOURS_CONFIG } from '../config/activeHours';

/**
 * Calcula horas ativas (9h-22h) decorridas entre dois timestamps
 * Replica: src/utils/penalty_v2.py::calculate_active_hours_overdue
 *
 * @param lastSaleTimestamp - ISO string da última venda
 * @param currentTimestamp - ISO string do timestamp atual
 * @returns Número de horas ativas decorridas
 */
export function calculateActiveHoursSinceSale(
  lastSaleTimestamp: string,
  currentTimestamp: string
): number {
  const ACTIVE_START = ACTIVE_HOURS_CONFIG.START;
  const ACTIVE_END = ACTIVE_HOURS_CONFIG.END;

  const lastSale = new Date(lastSaleTimestamp);
  const current = new Date(currentTimestamp);

  if (lastSale >= current) {
    return 0;
  }

  let activeHours = 0;

  // Preparar datas zeradas para comparação de dias
  const lastSaleDay = new Date(lastSale);
  lastSaleDay.setUTCHours(0, 0, 0, 0);

  const currentDay = new Date(current);
  currentDay.setUTCHours(0, 0, 0, 0);

  let currentDate = new Date(lastSaleDay);

  while (currentDate <= currentDay) {
    const isFirstDay = currentDate.getTime() === lastSaleDay.getTime();
    const isLastDay = currentDate.getTime() === currentDay.getTime();

    if (isFirstDay) {
      // Primeiro dia: da última venda até fim do horário ativo
      if (isLastDay) {
        // Mesmo dia: da última venda até agora
        const startHour = Math.max(
          ACTIVE_START,
          lastSale.getUTCHours() + lastSale.getUTCMinutes() / 60
        );
        const endHour = Math.min(
          ACTIVE_END,
          current.getUTCHours() + current.getUTCMinutes() / 60
        );
        activeHours += Math.max(0, endHour - startHour);
      } else {
        // Dia da venda: da venda até 22:00
        const startHour = Math.max(
          ACTIVE_START,
          lastSale.getUTCHours() + lastSale.getUTCMinutes() / 60
        );
        activeHours += Math.max(0, ACTIVE_END - startHour);
      }
    } else if (isLastDay) {
      // Último dia: de 09:00 até agora
      const endHour = Math.min(
        ACTIVE_END,
        current.getUTCHours() + current.getUTCMinutes() / 60
      );
      activeHours += Math.max(0, endHour - ACTIVE_START);
    } else {
      // Dias intermediários: 13 horas completas (09:00-22:00)
      activeHours += (ACTIVE_END - ACTIVE_START);
    }

    currentDate.setUTCDate(currentDate.getUTCDate() + 1);
  }

  return activeHours;
}

/**
 * Calcula expectativa de horas até próxima venda baseada em DPD 7
 *
 * @param dpd7 - Demand Per Day médio dos últimos 7 dias
 * @returns Expectativa em horas
 */
export function calculateExpectedHours(dpd7: number): number {
  const ACTIVE_HOURS_PER_DAY = ACTIVE_HOURS_CONFIG.TOTAL_HOURS;
  const MIN_DPD = 0.01; // Mínimo técnico para evitar divisão por zero

  return ACTIVE_HOURS_PER_DAY / Math.max(dpd7, MIN_DPD);
}

/**
 * Formata horas em string legível (Xh Ymin ou Xd Yh)
 *
 * @param hours - Número de horas
 * @returns String formatada
 */
export function formatTimeFromHours(hours: number): string {
  if (hours < 0) return '0h';

  const days = Math.floor(hours / 24);
  const remainingHours = Math.floor(hours % 24);
  const minutes = Math.round((hours % 1) * 60);

  if (days > 0) {
    if (remainingHours > 0) {
      return `${days}d ${remainingHours}h`;
    }
    return `${days}d`;
  }

  if (remainingHours > 0) {
    if (minutes > 0) {
      return `${remainingHours}h${minutes.toString().padStart(2, '0')}min`;
    }
    return `${remainingHours}h`;
  }

  return `${minutes}min`;
}

/**
 * Calcula quando começou a contagem de horário ativo
 * (se a venda foi fora do horário 9h-22h)
 *
 * @param lastSaleTimestamp - ISO string da última venda
 * @returns Informação sobre início da contagem
 */
export function getActiveCountingStart(lastSaleTimestamp: string | null): {
  isOutsideActiveHours: boolean;
  activeStartTime: Date | null;
} {
  if (!lastSaleTimestamp) {
    return { isOutsideActiveHours: false, activeStartTime: null };
  }

  const lastSale = new Date(lastSaleTimestamp);
  const lastSaleHour = lastSale.getUTCHours();

  const ACTIVE_START = ACTIVE_HOURS_CONFIG.START;
  const ACTIVE_END = ACTIVE_HOURS_CONFIG.END;

  // Se a última venda foi fora do horário ativo
  if (lastSaleHour < ACTIVE_START || lastSaleHour >= ACTIVE_END) {
    const activeStart = new Date(lastSale);

    if (lastSaleHour < ACTIVE_START) {
      // Venda antes das 9h - contagem começa às 9h do mesmo dia
      activeStart.setUTCHours(ACTIVE_START, 0, 0, 0);
    } else {
      // Venda depois das 22h - contagem começa às 9h do dia seguinte
      activeStart.setUTCDate(activeStart.getUTCDate() + 1);
      activeStart.setUTCHours(ACTIVE_START, 0, 0, 0);
    }

    return { isOutsideActiveHours: true, activeStartTime: activeStart };
  }

  return { isOutsideActiveHours: false, activeStartTime: null };
}
